import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef
} from '@angular/material/dialog';
import { generateUUID } from 'src/app/core/generators/generateUUID';
import { IProduto } from 'src/app/services/ProdutoService/produto.model';
import { ProdutoService } from 'src/app/services/ProdutoService/produto.service';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';
import { VendaEstoqueService } from 'src/app/services/VendaEstoqueService/venda-estoque.service';
import { ImpressoraService } from 'src/app/services/ImpressoraService/impressora.service';
import {
  abrevUnidadeMedida,
  labelUnidadeMedida,
  MODO_PAGAMENTO_OPTIONS
} from '../../estoque/estoque.constants';

type IOptions = { name: string; value: number | null };

const TIPO_PAGAMENTO_A_PRAZO = 28;

@Component({
  selector: 'app-modal-venda-estoque',
  templateUrl: './modal-venda-estoque.component.html',
  styleUrls: ['./modal-venda-estoque.component.scss']
})
export class ModalVendaEstoqueComponent implements OnInit {
  form: FormGroup;
  isLoading = false;
  guid = generateUUID();
  produtos: IProduto[] = [];
  produtoOptions: IOptions[] = [];
  modoPagamentoOptions = MODO_PAGAMENTO_OPTIONS;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { PK_CAIX?: number },
    public dialogRef: MatDialogRef<ModalVendaEstoqueComponent>,
    private fb: FormBuilder,
    private produtoService: ProdutoService,
    private vendaEstoqueService: VendaEstoqueService,
    private impressoraService: ImpressoraService,
    public swalAlert: SwalAlertService
  ) {
    this.form = this.initForm();
  }

  ngOnInit(): void {
    this.form.get('VL_ACRM')?.valueChanges.subscribe(() => this.calcValores());
    this.form.get('VL_DSCT')?.valueChanges.subscribe(() => this.calcValores());
    this.Itens.valueChanges.subscribe(() => this.calcValores());
    this.loadProdutos();
    this.addItem();
  }

  initForm(): FormGroup {
    return this.fb.group({
      TX_CLNT: [''],
      Itens: this.fb.array([]),
      ServicoPagamentoTipoValor: this.fb.array([]),
      VL_ACRM: [0],
      VL_DSCT: [0],
      NU_TIPO_PGMT: [0],
      VL_PGMT: [0],
      VL_TOTL: [0],
      VL_REST: [0]
    });
  }

  get Itens(): FormArray {
    return this.form.get('Itens') as FormArray;
  }

  get ServicoPagamentoTipoValor(): FormArray {
    return this.form.get('ServicoPagamentoTipoValor') as FormArray;
  }

  get clienteObrigatorio(): boolean {
    return this.ServicoPagamentoTipoValor.value.some(
      (p: any) => Number(p.NU_TIPO_PGMT) === TIPO_PAGAMENTO_A_PRAZO
    );
  }

  get labelCliente(): string {
    return this.clienteObrigatorio ? 'Cliente (obrigatório p/ a prazo)' : 'Cliente (opcional)';
  }

  loadProdutos(): void {
    this.isLoading = true;
    this.produtoService.GetProdutos().subscribe({
      next: (produtos: IProduto[]) => {
        this.produtos = produtos.filter(p => p.NU_STTS === 1);
        this.produtoOptions = this.produtos.map(p => ({
          name: `${p.NM_PROD} (${abrevUnidadeMedida(p.NU_UNID_MED)} · saldo: ${p.QT_SALD ?? 0})`,
          value: p.PK_PROD
        }));
        this.Itens.controls.forEach(ctrl =>
          this.updateItemTotal(ctrl as FormGroup, true)
        );
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  createItemGroup(): FormGroup {
    return this.fb.group({
      FK_PROD: [null, Validators.required],
      QT_VEND: [1, [Validators.required, Validators.min(1)]],
      VL_UNIT: [0],
      VL_TOTL: [0]
    });
  }

  addItem(): void {
    const group = this.createItemGroup();
    group.get('FK_PROD')?.valueChanges.subscribe(() => this.updateItemTotal(group, true));
    group.get('QT_VEND')?.valueChanges.subscribe(() => this.updateItemTotal(group));
    group.get('VL_UNIT')?.valueChanges.subscribe(() => this.updateItemTotal(group));
    this.Itens.push(group);
    this.calcValores();
  }

  removeItem(index: number): void {
    if (this.Itens.length <= 1) return;
    this.Itens.removeAt(index);
    this.calcValores();
  }

  getProdutoDoItem(group: FormGroup | AbstractControl): IProduto | undefined {
    const fkProd = Number(group.get('FK_PROD')?.value);
    return this.produtos.find(p => p.PK_PROD === fkProd);
  }

  labelQuantidade(group: FormGroup | AbstractControl): string {
    const produto = this.getProdutoDoItem(group);
    if (!produto) return 'Quantidade';
    return `Quantidade (${abrevUnidadeMedida(produto.NU_UNID_MED)})`;
  }

  labelUnidadeItem(group: FormGroup | AbstractControl): string {
    const produto = this.getProdutoDoItem(group);
    return produto ? labelUnidadeMedida(produto.NU_UNID_MED) : '---';
  }

  updateItemTotal(group: FormGroup, fromProduct = false): void {
    const fkProd = Number(group.get('FK_PROD')?.value);
    const qty = Number(group.get('QT_VEND')?.value || 0);
    const produto = this.produtos.find(p => p.PK_PROD === fkProd);
    const unit = fromProduct && produto
      ? Number(produto.VL_UNIT)
      : Number(group.get('VL_UNIT')?.value || 0);
    group.patchValue({ VL_UNIT: unit, VL_TOTL: Number((unit * qty).toFixed(2)) }, { emitEvent: false });
    this.calcValores();
  }

  calcValores(): void {
    const subtotal = this.Itens.controls.reduce((acc, ctrl) => {
      const group = ctrl as FormGroup;
      return acc + Number(group.get('VL_TOTL')?.value || 0);
    }, 0);

    const acrescimo = Number(this.form.get('VL_ACRM')?.value || 0);
    const desconto = Number(this.form.get('VL_DSCT')?.value || 0);
    const valorTotal = Math.max(Number((subtotal + acrescimo - desconto).toFixed(2)), 0);

    this.form.get('VL_TOTL')?.setValue(valorTotal, { emitEvent: false });
    this.calcValorRestante();
  }

  calcValorRestante(): void {
    const valorTotal = Number(this.form.get('VL_TOTL')?.value || 0);
    const valorAdicionado = this.ServicoPagamentoTipoValor.value.reduce(
      (acc: number, item: any) => acc + Number(item.VL_PGMT_SAID || 0),
      0
    );
    const valorRestante = Math.max(Number((valorTotal - valorAdicionado).toFixed(2)), 0);
    this.form.get('VL_REST')?.setValue(valorRestante, { emitEvent: false });
  }

  onChangeModoPagamento(tipo: number | null): void {
    const valorRestante = Number(this.form.get('VL_REST')?.value || 0);
    if (tipo && valorRestante > 0) {
      this.form.get('VL_PGMT')?.setValue(valorRestante, { emitEvent: false });
    }
  }

  addPagamento(): void {
    const valorRestante = Number(this.form.get('VL_REST')?.value || 0);
    const valorParaAdicionar = Number(this.form.get('VL_PGMT')?.value || 0);
    const tipoPagamento = Number(this.form.get('NU_TIPO_PGMT')?.value || 0);

    if (!tipoPagamento || valorParaAdicionar <= 0 || valorRestante <= 0) return;

    if (valorParaAdicionar > valorRestante + 0.001) {
      this.swalAlert.showAlert(
        'Atenção!',
        'O valor do pagamento não pode ser maior que o valor restante.',
        'warning'
      );
      return;
    }

    const pagamento = this.modoPagamentoOptions.find(o => Number(o.value) === tipoPagamento);
    if (!pagamento) return;

    this.ServicoPagamentoTipoValor.push(
      this.fb.group({
        NM_TIPO_PGMT: [pagamento.name],
        NU_TIPO_PGMT: [pagamento.value, Validators.required],
        VL_PGMT_SAID: [Number(valorParaAdicionar.toFixed(2)), Validators.required]
      })
    );

    this.form.patchValue({ NU_TIPO_PGMT: 0, VL_PGMT: 0 });
    this.calcValorRestante();
  }

  removePagamento(index: number): void {
    this.ServicoPagamentoTipoValor.removeAt(index);
    this.calcValorRestante();
  }

  private getNomeCliente(): string {
    return String(this.form.get('TX_CLNT')?.value ?? '').trim();
  }

  disableConfirmar(): boolean {
    const valorRestante = Number(this.form.get('VL_REST')?.value || 0);
    const faltaClienteAPrazo = this.clienteObrigatorio && !this.getNomeCliente();
    return (
      this.Itens.length === 0 ||
      this.Itens.invalid ||
      this.ServicoPagamentoTipoValor.length === 0 ||
      valorRestante !== 0 ||
      faltaClienteAPrazo
    );
  }

  confirmarVenda(): void {
    if (this.disableConfirmar()) return;

    const funcionarioId = Number(localStorage.getItem('id') ?? 0);
    if (!funcionarioId) {
      this.swalAlert.showAlert('Atenção!', 'Usuário não identificado.', 'warning');
      return;
    }

    const nomeCliente = this.getNomeCliente();
    if (this.clienteObrigatorio && !nomeCliente) {
      this.swalAlert.showAlert(
        'Atenção!',
        'Informe o cliente para venda a prazo.',
        'warning'
      );
      return;
    }

    for (const ctrl of this.Itens.controls) {
      const group = ctrl as FormGroup;
      const fkProd = Number(group.get('FK_PROD')?.value);
      const qty = Number(group.get('QT_VEND')?.value || 0);
      const produto = this.produtos.find(p => p.PK_PROD === fkProd);
      const saldo = Number(produto?.QT_SALD ?? 0);
      if (qty > saldo) {
        this.swalAlert.showAlert(
          'Atenção!',
          `Saldo insuficiente para ${produto?.NM_PROD ?? 'o produto'}. Disponível: ${saldo}.`,
          'warning'
        );
        return;
      }
    }

    this.isLoading = true;
    this.vendaEstoqueService
      .CreateVendaEstoque(
        {
          FK_FUNC_OPER: funcionarioId,
          TX_CLNT: nomeCliente || undefined,
          VL_DSCT: Number(this.form.get('VL_DSCT')?.value || 0) || undefined,
          VL_ACRM: Number(this.form.get('VL_ACRM')?.value || 0) || undefined,
          Itens: this.Itens.getRawValue().map((item: any) => ({
            FK_PROD: Number(item.FK_PROD),
            QT_VEND: Number(item.QT_VEND),
            VL_UNIT: Number(item.VL_UNIT) || undefined
          })),
          ServicoPagamentoTipoValor: this.ServicoPagamentoTipoValor.value.map((item: any) => ({
            NU_TIPO_PGMT: Number(item.NU_TIPO_PGMT),
            VL_PGMT_SAID: Number(Number(item.VL_PGMT_SAID).toFixed(2))
          }))
        },
        this.guid
      )
      .subscribe({
        next: venda => {
          const pkVendEstq = Number(venda?.PK_VEND_ESTQ ?? 0);
          if (pkVendEstq > 0) {
            this.imprimirReciboVenda(pkVendEstq, () => {
              this.isLoading = false;
              this.dialogRef.close(true);
            });
            return;
          }
          this.isLoading = false;
          this.dialogRef.close(true);
        },
        error: err => {
          this.isLoading = false;
          const msg =
            err?.error?.Errors?.Error ??
            err?.error?.Error ??
            err?.error?.message ??
            'Não foi possível registrar a venda.';
          this.swalAlert.showAlert('Erro!', msg, 'warning');
        }
      });
  }

  private imprimirReciboVenda(pkVendEstq: number, onDone: () => void): void {
    this.vendaEstoqueService.GetReceiptVendaEstoqueById(pkVendEstq, 1).subscribe({
      next: response => {
        if (response?.ReportBase64) {
          this.impressoraService.impressoraTicket(response.ReportBase64, 'text/plain');
        }
        onDone();
      },
      error: () => {
        this.swalAlert.showAlert(
          'Atenção!',
          'Venda registrada, mas não foi possível gerar o recibo.',
          'warning'
        );
        onDone();
      }
    });
  }

  onCancelar(): void {
    this.dialogRef.close(false);
  }
}
